/**
 * People, teams and roles: the user rows, the full personnel record, and their writes.
 */
import { del, get, post, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ApiUser {
  /** plan 0027 batch A: the birth date is unknown, so the person cannot be put on new shifts */
  birth_date_missing?: boolean;
  /** YYYY-MM-DD; only for a reader with people.manage */
  birth_date?: string | null;
  /** still skolepliktig; null not known (plan 0027 batch A). Only for a reader with people.manage */
  in_compulsory_school?: boolean | null;
  /** the sykt barn quota's facts, ftrl § 9-6 (plan 0027 batch A); null not known. people.manage only */
  children_under_12?: number | null;
  /** the earlier employment in this company, ftrl § 8-18 (2) (plan 0027 batch I); people.manage only */
  previous_employment_start?: string | null;
  previous_employment_end?: string | null;
  sole_carer?: boolean | null;
  chronically_ill_children?: number | null;
  /** AML §10-12, from the membership; only for a reader with people.manage (2026-09-23) */
  exempt_position?: 'leading' | 'independent' | null;
  /** AML §10-5, from the membership; only for a reader with people.manage */
  averaging?: 'individual' | 'tillitsvalgte' | null;
  /** AML §10-5 period (plan 0027 batch E, #112): first day YYYY-MM-DD, weeks, average week, plan per week; null when not entered */
  averaging_start?: string | null;
  averaging_weeks?: number | null;
  averaging_target?: number | null;
  averaging_plan?: number[] | null;
  /** AML §10-12: the company's stated reason, and who set it when; same reader */
  exempt_reason?: string | null;
  exempt_set_by_name?: string | null;
  exempt_set_at?: string | null;
  /** AML §10-6(12), plan 0027 G2: the date the written avspasering agreement took effect, null for none */
  avspasering_agreed_on?: string | null;
  /** the share of overtime the agreement covers, percent; null reads as 100 */
  avspasering_share?: number | null;
  /** plan 0027 G2 slice 4: the date the flexitime agreement took effect (fastlønn only), null for none */
  flex_agreed_on?: string | null;
  /** aml § 14-9, from the membership (#108); only for a reader with people.manage; null when not entered */
  employment_form?: 'fast' | 'midlertidig' | 'tilkalling' | null;
  /** aml § 14-6 (1) e: the last day of a temporary job, YYYY-MM-DD; same reader */
  end_date?: string | null;
  /** the person's number in this company's payroll system (Lønns-ID, 2026-10-16); every payroll file and send uses it before employee_id; same reader */
  payroll_id?: string | null;
  id: number;
  username: string;
  name: string;
  lastname: string | null;
  email?: string | null;
  /** the last session (users.last_login_at); only the administrator's read carries it (#130) */
  last_login_at?: string | null;
  /** the newest welcome link, mailed or shown (#120); the administrator's read only */
  invited_at?: string | null;
  /** the phone app is registered for push (#120); the administrator's read only */
  has_app?: boolean;
  employee_id?: string | null;
  role: string;
  contract_type: 'hourly' | 'monthly' | string | null;
  active_status: boolean;
  /** the agreed week as this company has it: the person's own, else the company's (plan 0027 B) */
  max_hours_per_week: number | null;
  /** the person's own agreed week in this company; null follows the company's (plan 0027 B) */
  agreed_week_hours?: number | null;
  teams?: { id: number; name: string }[];
  /** the work phone every colleague may see (User.colleagueView, owner 2026-09-20) */
  phone?: string | null;
  /** the job title the company prints on a rota, or its own name for the role */
  title?: string | null;
  /** the fixed salary this company pays, to those who see pay (GET /users, 2026-10-29) */
  fixed_salary_here?: number;
}
export interface ApiTeam {
  id: number;
  name: string;
}
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    // the route answers an array today; the old app also accepted { users }
    queryFn: async () => {
      const r = await get<ApiUser[] | { users: ApiUser[] }>('/users');
      return Array.isArray(r) ? r : r.users;
    },
    staleTime: 60_000,
  });
}
/**
 * The company's teams. GET /admin/teams asks for schedule.view, because the schedule is
 * what filters by team, so a role that manages people without seeing the schedule cannot
 * read them and is not asked to: the team picker is simply absent rather than erroring.
 * Whether managing people should carry the team list is a mapping question, reported.
 */
export function useTeams() {
  const can = useCan();
  return useQuery({
    queryKey: ['teams'],
    queryFn: () => get<ApiTeam[]>('/admin/teams'),
    enabled: can('vaktplan.se'),
    staleTime: 5 * 60_000,
  });
}
/** GET /admin/users/:id, the only payload with personal_number and the rates. */
export interface ApiUserFull extends ApiUser {
  phone: string | null;
  address: string | null;
  email: string | null;
  employee_id: string | null;
  personal_number?: string | null;
  /** "Ansatt fra" in the active company, YYYY-MM-DD; sick pay waits four weeks from it (ftrl §8-18) */
  employment_start?: string | null;
  gender: string | null;
  hourly_rate: string | null;
  monthly_rate: string | null;
  vacation_days_balance: number | null;
  next_year_vacation_days_balance: number | null;
  total_vacation_days_per_year: number | null;
  /** memberships as ids and roles only; the server names no company the caller is not in */
  userTenantsList?: { tenant_id: number; role: string }[];
}
export function useUser(id: number | null) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => get<ApiUserFull>(`/admin/users/${id}`),
    enabled: id != null,
  });
}
/** GET /admin/users/:id/averaging (people.manage): the §10-5 period and where it stands (#112). */
export interface AveragingStatus {
  averaging: 'individual' | 'tillitsvalgte' | null;
  period: { start: string; weeks: number; target: number; plan: number[] } | null;
  /** per week: the plan, and the hours of its shifts where it has any (null: counted at the plan) */
  weeks?: { week: string; plan: number; hours: number | null }[];
  total?: number;
  average?: number;
  /** hours past the agreed average by the period's end; negative is under */
  over?: number;
}
export function useAveraging(id: number | null, enabled = true) {
  return useQuery({
    queryKey: ['averaging', id],
    queryFn: () => get<AveragingStatus>(`/admin/users/${id}/averaging`),
    enabled: id != null && enabled,
  });
}
export type UserWrite = Partial<
  Pick<
    ApiUserFull,
    | 'username'
    | 'name'
    | 'lastname'
    | 'phone'
    | 'address'
    | 'email'
    | 'hourly_rate'
    | 'monthly_rate'
    | 'gender'
    | 'contract_type'
    | 'active_status'
    | 'max_hours_per_week'
    | 'vacation_days_balance'
    | 'next_year_vacation_days_balance'
    | 'total_vacation_days_per_year'
    | 'employee_id'
    | 'personal_number'
    | 'birth_date'
    | 'in_compulsory_school'
    | 'children_under_12'
    | 'sole_carer'
    | 'chronically_ill_children'
    | 'employment_start'
    | 'previous_employment_start'
    | 'previous_employment_end'
    | 'exempt_position'
    | 'averaging'
    | 'averaging_start'
    | 'averaging_weeks'
    | 'averaging_target'
    | 'averaging_plan'
    | 'exempt_reason'
    | 'agreed_week_hours'
    | 'avspasering_agreed_on'
    | 'avspasering_share'
    | 'flex_agreed_on'
    | 'employment_form'
    | 'end_date'
    | 'payroll_id'
  >
> & {
  team_ids?: number[];
  password?: string;
  /** create only: mail a 7-day link to set their own password; no password is needed then (2026-09-21) */
  send_welcome?: boolean;
  /** scoped membership intent, TF-PRESERVATION-FINAL: only companies the caller administers, everything omitted is preserved */
  memberships?: { add: number[]; remove: number[] };
};
export function useUserWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['users'] }),
      qc.invalidateQueries({ queryKey: ['user'] }),
      // the avspasering agreement moves the bank (plan 0027 G2)
      qc.invalidateQueries({ queryKey: ['timebank'] }),
      // the §10-5 period moves where it stands (#112)
      qc.invalidateQueries({ queryKey: ['averaging'] }),
    ]);
  return {
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: UserWrite }) =>
        put<ApiUserFull>(`/admin/users/${id}`, body),
      onSettled: done,
    }),
    create: useMutation({
      mutationFn: (body: UserWrite) =>
        post<ApiUserFull & { welcome?: 'sent' | 'failed' | 'unavailable' }>('/admin/users', body),
      onSettled: done,
    }),
    /** account mail from the employee page: a 7-day welcome link or the 60-minute reset link */
    mail: useMutation({
      mutationFn: ({ id, kind }: { id: number; kind: 'welcome' | 'reset-link' }) =>
        post<{ ok: true; to: string }>(`/admin/users/${id}/${kind}`, {}),
      // the list's «Invitert» date comes from the server (#120)
      onSettled: done,
    }),
    /** the welcome to everyone not yet in (#120); `again` resends to those invited over a day ago */
    inviteAll: useMutation({
      mutationFn: (again: boolean) =>
        post<{ sent: number; failed: number; without_email: number }>('/admin/invitations', { again }),
      onSettled: done,
    }),
  };
}
/** «Melding» from the Vaktplan person panel: a push and a row in the person's varsler, or e-mail without the app */
export const sendMessage = (id: number, text: string) =>
  post<{ delivered: 'push' | 'email' | 'none' }>(`/admin/users/${id}/message`, { text });

export function useMe(id: number | undefined) {
  return useQuery({
    queryKey: ['me', id],
    queryFn: () => get<ApiUserFull>(`/users/${id}`),
    enabled: id != null,
    staleTime: 60_000,
  });
}
/**
 * The company's roles (docs/design/2026-09-16-custom-roles.md). Reading is open to any
 * member, because a screen has to be able to say which role somebody holds; writing is the
 * administrator's, and `may_edit` says whether this caller is one. `permissions` is the
 * vocabulary the server enforces, with what each one rests on, so the editor never offers a
 * checkbox the server does not know.
 */
export interface ApiRole {
  key: string;
  name: string;
  is_system: boolean;
  permissions: string[];
  revision: number;
  members: number;
}
export interface ApiPermission {
  key: string;
  name: string;
  detail: string;
  requires: string[];
}
export interface RolesAnswer {
  roles: ApiRole[];
  permissions: ApiPermission[];
  baseline: string;
  may_edit: boolean;
}
/** The active company's roles, or a named company the caller is a member of. */
export function useRoles(tenantId?: number) {
  return useQuery({
    queryKey: ['roles', tenantId ?? 'active'],
    queryFn: () => get<RolesAnswer>(tenantId ? `/admin/roles?tenant_id=${tenantId}` : '/admin/roles'),
    staleTime: 30_000,
  });
}

/**
 * Creating, editing and deleting a role. Every one of them can change what somebody on this
 * screen may do, including the person doing it, so all three invalidate the company's own
 * answer as well as the role list: `me.permissions` comes from GET /tenant.
 */
export function useRoleWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['roles'] }),
      qc.invalidateQueries({ queryKey: ['tenant'] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
      qc.invalidateQueries({ queryKey: ['user'] }),
    ]);
  return {
    create: useMutation({
      mutationFn: (b: { key: string; name: string; permissions: string[] }) =>
        post<ApiRole>('/admin/roles', b),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({
        key,
        body,
      }: { key: string; body: { name?: string; permissions?: string[]; revision: number } }) =>
        put<ApiRole>(`/admin/roles/${encodeURIComponent(key)}`, body),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: (key: string) => del<unknown>(`/admin/roles/${encodeURIComponent(key)}`),
      onSettled: done,
    }),
  };
}
export function useTeamWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['teams'] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
    ]);
  return {
    create: useMutation({
      mutationFn: (name: string) => post<ApiTeam>('/admin/teams', { name }),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, name }: { id: number; name: string }) =>
        put<ApiTeam>(`/admin/teams/${id}`, { name }),
      onSettled: done,
    }),
    remove: useMutation({ mutationFn: (id: number) => del<unknown>(`/admin/teams/${id}`), onSettled: done }),
  };
}
